import { useState, useEffect, useMemo, useRef, createRef } from 'react';
import { useMatch } from 'react-router-dom';

import PropTypes from 'prop-types';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Material UI
import { Grid, IconButton, Icon, Stack, Tooltip, TextField, Button } from '@mui/material';

// Componentes
import CustomTable from '@components/Tables/CustomTable';
import CustomTabs from '@components/Tabs/CustomTabs';
import AdvancedSelect from '@components/Selects/AdvancedSelect';
import SesionesButton from '@components/Events/SesionesButton';
import QRPreregistro from '@components/Events/QRPreregistro';
import CustomModal from '@components/Modal/CustomModal';
import CardEvent from '@components/Events/CardEvent';
import EventForm from '@components/Events/EventForm';
import { showLoading } from '@components/LinearProgress/CustomLoading';
import BasicSelect from '@components/Selects/BasicSelect';

// Services y utils
import EventsServices from '@services/EventsServices';
import ReconocimientosServices from '@services/ReconocimientosServices';
import { Swal, SwalImage } from '@utils/alerts';
import { convertBase64 } from '@utils/Utilities';

// Middleware
import middleware from '@middlewares/middleware';

const CardListEvent = (props) => {
  const {
    post,
    show,
    setShow = () => {},
    type,
    setType = () => {},
    handleClose = () => {},
    getList = () => {},
    cat,
    isLoadingCat,
    URL = '',
  } = props;

  const basicColumns = [
    { id: 'entidad', label: 'Entidad' },
    { id: 'municipio', label: 'Municipio' },
    { id: 'nombre', label: 'Nombre' },
    { id: 'FechaRegistro', label: 'Fecha Registro' },
    { id: 'FechaNacimiento', label: 'Fecha de Nacimiento' },
    { id: 'sexoClave', label: 'Sexo' },
    { id: 'celular', label: 'Celular' },
    { id: 'email', label: 'Correo' },
  ];

  const [columns, setColumns] = useState(basicColumns);
  const [newColumns, setNewColumns] = useState({});

  const [rowsAttended, setRowsAttended] = useState([]);
  const [rowsInvited, setRowsInvited] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [search1, setSearch1] = useState({ nombre: '', celular: '', idMunicipio: 0, municipio: '' });
  const [search2, setSearch2] = useState({ nombre: '', celular: '', idMunicipio: 0, municipio: '' });

  const [selectedUser, setSelectedUser] = useState([]);
  const [open, setOpen] = useState(false);

  const [cat_plantilla, setCatalog] = useState([]);
  const [openPlantilla, setOpenPlantilla] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [isLoadingPlantilla, setIsLoadingPlantilla] = useState(false);
  const [errorPlantilla, setErrorPlantilla] = useState(false);
  const [force, setForce] = useState(false);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [show]);

  const formatColumns = (array) => array?.map((item) => ({ ...item, maxWidth: 200, tooltip: true }));

  useEffect(() => {
    if (newColumns.invitedQuestions || newColumns.attendedQuestions) {
      const extraCols = [
        {
          id: 'qr',
          label: 'QR',
        },
        /* {
          id: 'reconocimiento',
          label: 'Reconocimiento',
        }, */
      ];

      if (show + '' === '0')
        setColumns([
          ...basicColumns,
          { id: 'totalAsistencias', label: 'Asistencias' },
          ...formatColumns(newColumns.invitedQuestions ?? []),
          ...extraCols,
        ]);
      else
        setColumns([
          ...basicColumns,
          { id: 'total', label: 'Asistencias' },
          ...formatColumns(newColumns.attendedQuestions ?? []),
        ]);
    }
    // eslint-disable-next-line
  }, [show, newColumns]);

  const handleOpenPlantilla = (item) => {
    setSelectedUser(item);
    setOpenPlantilla(true);
  };

  const generarReconocimientos = async (user, type, plantilla) => {
    try {
      setIsLoading(true);
      const showNumber = Number(show);

      let params = {
        idEvento: post.id,
        idPlantilla: !force ? plantilla : 1,
        forceGenerate: force,
        getAttended: showNumber === 0 ? false : true,
        getInvited: showNumber === 0 ? true : false,
      };

      if ([2, 3].includes(type)) {
        params.idUsuario = user.id;
      }

      const result = await EventsServices.generateReconocimientos(params);
      const { results, response, message } = result;

      if (!results) throw new Error(message);

      const archivos = response?.data || [];

      if (!archivos.length) {
        throw new Error('No hay reconocimientos para descargar');
      }

      const mapaUsuarios = {};
      rowsInvited.forEach((u) => {
        mapaUsuarios[u.id] = u.nombre;
      });

      const limpiarNombre = (nombre) => nombre.replace(/[<>:"/\\|?*]+/g, '').trim();

      if (archivos.length === 1) {
        const archivo = archivos[0];

        if (!archivo.fileURL) throw new Error('Archivo inválido');

        const resp = await fetch(archivo.fileURL);
        if (!resp.ok) throw new Error('Error al descargar archivo');

        const blob = await resp.blob();

        const nombreUsuario = mapaUsuarios[archivo.idUsuario] || `usuario-${archivo.idUsuario}`;

        const nombreLimpio = limpiarNombre(nombreUsuario);

        const extension = archivo.fileName?.split('.').pop() || 'docx';

        const nombreFinal = `${nombreLimpio}.${extension}`;

        saveAs(blob, nombreFinal);

        Swal.fire({
          title: 'Descarga completa',
          text: 'Archivo descargado correctamente',
          icon: 'success',
        });

        return;
      }

      const zip = new JSZip();

      const contadorNombres = {};

      await Promise.all(
        archivos.map(async (archivo) => {
          try {
            if (!archivo.fileURL) return;

            const resp = await fetch(archivo.fileURL);
            if (!resp.ok) return;

            const blob = await resp.blob();

            const nombreUsuario = mapaUsuarios[archivo.idUsuario] || `usuario-${archivo.idUsuario}`;

            const nombreBase = limpiarNombre(nombreUsuario);

            const extension = archivo.fileName?.split('.').pop() || 'docx';

            contadorNombres[nombreBase] = (contadorNombres[nombreBase] || 0) + 1;

            const nombreFinal =
              contadorNombres[nombreBase] > 1
                ? `${nombreBase} (${contadorNombres[nombreBase]}).${extension}`
                : `${nombreBase}.${extension}`;

            zip.file(nombreFinal, blob);
          } catch (e) {
            console.warn('Error con archivo:', archivo.fileName);
          }
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const hoy = new Date();
      const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;

      const nombreZip = `Reconocimientos_${post.Actividad}_${fecha}.zip`;

      saveAs(zipBlob, nombreZip);

      Swal.fire({
        title: 'Descarga completa',
        text: message,
        icon: 'success',
      });
    } catch (error) {
      Swal.fire({ title: error.message, icon: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const catalogs = async () => {
    try {
      setIsLoadingPlantilla(true);
      const result = await ReconocimientosServices.getCatPlantillas();
      const { results, message, response } = result;
      if (results) {
        setCatalog(response.data);
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Swal.fire({ title: error.message, icon: 'warning' });
    } finally {
      setIsLoadingPlantilla(false);
    }
  };

  const getData = async () => {
    let API = EventsServices.getEventosDetailsAttended;
    if ('' + show === '0') API = EventsServices.getEventosDetailsInvited;

    try {
      setIsLoading(true);
      const result = await API({ idEvento: post.id });
      const { success, results, response, message } = result;

      if (success) {
        if (results) {
          const canEdit = middleware.checkMenuAction('Editar');

          // 🔥 Mapa para saber qué campos son archivo
          const fileFieldsMap = {};
          (response.invitedQuestions ?? []).forEach((q) => {
            fileFieldsMap[q.id] = { isFile: q.isFile, allowTypes: q.allowTypes };
          });
          (response.attendedQuestions ?? []).forEach((q) => {
            fileFieldsMap[q.id] = { isFile: q.isFile, allowTypes: q.allowTypes };
          });

          const transformData = (list, sesionesKey, totalKey) =>
            list.map((item, index) => {
              const fileFields = Object.fromEntries(
                Object.keys(fileFieldsMap).map((key) => {
                  const idPregunta = Number(key);
                  const value = item[key] ?? null;
                  const fileInputRef = createRef();

                  return [
                    key,
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={value ? 'Ver imagen' : 'Sin imagen'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!value}
                            onClick={() => {
                              if (value) {
                                SwalImage({ image: value, title: 'Respuesta', small: true });
                              }
                            }}
                          >
                            <Icon>visibility</Icon>
                          </IconButton>
                        </span>
                      </Tooltip>

                      <input
                        type="file"
                        accept={fileFieldsMap[idPregunta].allowTypes}
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          updateImage(item.id, idPregunta, file);
                        }}
                      />

                      <Tooltip title="Subir archivo">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        >
                          <Icon>upload</Icon>
                        </IconButton>
                      </Tooltip>
                    </Stack>,
                  ];
                })
              );

              return {
                ...item,
                ...fileFields,
                [totalKey]: (
                  <SesionesButton
                    data={{
                      idUser: item.id,
                      idEvento: post.id,
                      asistencia: item[sesionesKey],
                      sesiones: post.sesiones,
                      FechaFin: post.FechaFin,
                    }}
                    type="edit"
                    canEdit={canEdit}
                    onSave={getData}
                    enableColors
                    viewAll
                    TOLERANCIA_MIN={post.TOLERANCIA_MIN}
                  />
                ),
                qr: (
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ minWidth: 200 }}
                    startIcon={<Icon>qr_code</Icon>}
                    onClick={() => {
                      setSelectedUser([
                        {
                          Folio: item.Folio,
                          UUID: item.UUID,
                          NombreCompleto: item.NombreSolo + ' ' + item.Paterno + ' ' + item.Materno,
                        },
                        { imgPlantillaQR: post.imgPlantillaQR, soloMenores: post.soloMenores },
                      ]);
                      setOpen(true);
                    }}
                  >
                    Ver QR
                  </Button>
                ),
                /* reconocimiento: (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon>description</Icon>}
                      onClick={() => {
                        if (item.tieneCertificado === 1) {
                          Swal.fire({
                            title: 'Ya cuentas con un certificado existente',
                            text: '¿Qué deseas hacer?',
                            icon: 'warning',

                            showConfirmButton: true,
                            showCancelButton: true,
                            showDenyButton: true,

                            confirmButtonText: 'Descargar existente',
                            cancelButtonText: 'Generar nuevo',
                            denyButtonText: 'Cancelar',

                            confirmButtonColor: '#1976d2',
                            cancelButtonColor: '#6b7280',
                            denyButtonColor: '#ef4444',
                          }).then((result) => {
                            if (result.isConfirmed) {
                              //DESCRAGR EXISTENTE
                              generarReconocimientos(item, 2);
                              setForce(false);
                            } else if (result.dismiss === Swal.DismissReason.cancel) {
                              //GENERAR UNO NUEVO
                              handleOpenPlantilla(item);
                              setForce(true);
                            } else if (result.isDenied) {
                              //CANCELAR
                              setForce(false);
                              Swal.close();
                            }
                          });
                        } else {
                          handleOpenPlantilla(item);
                        }
                      }}
                    >
                      Generar
                    </Button>
                  </Stack>
                ), */
              };
            });

          setRowsInvited(transformData(response.invited ?? [], 'fechasAsistencias', 'totalAsistencias'));
          setRowsAttended(transformData(response.attended ?? [], 'fechas', 'total'));

          setNewColumns({
            invitedQuestions: response.invitedQuestions,
            attendedQuestions: response.attendedQuestions,
          });
        }
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateImage = async (userId, idPregunta, file) => {
    try {
      setIsLoading(true);
      const base64File = await convertBase64(file);
      let params = {
        userId,
        idPregunta,
        files: [{ file: base64File }],
      };
      const result = await EventsServices.updateImage(params);
      const { results, message } = result;
      if (results) {
        getData();
        Swal.fire({ title: message, icon: 'success' });
      } else throw new Error(message);
    } catch (error) {
      Swal.fire({ title: error.message, icon: 'error' });
    } finally {
      setIsLoading(true);
    }
  };

  const filter = (value, search) => {
    const normalizedValue = ('' + value)?.toLowerCase().trim().toLowerCase().replace(/\s+/g, '');
    const normalizedSearch = ('' + search)?.toLowerCase().trim().toLowerCase().replace(/\s+/g, '');
    const regex = new RegExp(normalizedSearch, 'i');
    return regex.test(normalizedValue);
  };

  const filteredRowsInvited = useMemo(() => {
    return rowsInvited.filter(({ nombre, municipio, celular }) => {
      return (
        filter(nombre, search1.nombre) &&
        filter(celular, search1.celular) &&
        (search1.idMunicipio > 0 ? filter(municipio, search1.municipio) : true)
      );
    });
  }, [rowsInvited, search1]);

  const filteredRowsAttended = useMemo(() => {
    return rowsAttended.filter(({ nombre, municipio, celular }) => {
      return (
        filter(nombre, search2.nombre) &&
        filter(celular, search2.celular) &&
        (search2.idMunicipio > 0 ? filter(municipio, search2.municipio) : true)
      );
    });
  }, [rowsAttended, search2]);

  const qrRef = useRef();

  const handleDeleteEvent = async (data) => {
    setIsLoading(true);
    const isLoading = showLoading({ message: 'Eliminando...' });

    try {
      const result = await EventsServices.deleteEvent({ idEvento: data.id });
      const { success, results, message } = result;

      if (success && results) {
        getList();
        handleClose();
        Swal.fire({ title: message, icon: 'success', customClass: { container: 'modal-alert' } });
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning', customClass: { container: 'modal-alert' } });
    } finally {
      setIsLoading(false);
      isLoading.close();
    }
  };

  const questionDelete = async (data) => {
    Swal.fire({
      title: 'Eliminar Evento',
      text: `¿Estas seguro de querer eliminar el evento: ${data.Actividad}?`,
      icon: 'warning',
      showDenyButton: true,
      showCancelButton: true,
      showConfirmButton: false,
      focusDeny: true,
      denyButtonText: 'Eliminar evento',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: { container: 'modal-alert' },
    }).then((res) => {
      if (res.isDenied) handleDeleteEvent(data);
    });
  };

  useEffect(() => {
    catalogs();
  }, []);

  return (
    <>
      {type === 2 && (
        <>
          <CardEvent
            post={post}
            setType={setType}
            getEvents={getList}
            handleDelete={questionDelete}
            URL={URL}
            handleReconocimientos={handleOpenPlantilla}
            canModify
          />

          <Grid container spacing={2} textAlign={'center'}>
            <CustomModal
              open={open}
              onClose={() => setOpen(false)}
              fullWidth
              title="Descargar QR"
              maxWidth="md"
              actionButtons={[
                {
                  icon: 'picture_as_pdf',
                  color: 'success',
                  title: 'Descargar en PDF',
                  action: () => qrRef.current.handleDownloadPDF(),
                },
                {
                  icon: 'image',
                  color: 'success',
                  title: 'Descargar en Imagen',
                  action: () => qrRef.current.handleDownloadImage(),
                },
              ]}
            >
              <QRPreregistro ref={qrRef} dataQR={selectedUser[0]} dataEvent={selectedUser[1]} view={true} />
            </CustomModal>

            <CustomModal
              open={openPlantilla}
              onClose={() => {
                setOpenPlantilla(false);
                setErrorPlantilla(false);
              }}
              fullWidth
              maxWidth="sm"
              disableClose
              title={
                <Stack spacing={1}>
                  <span style={{ fontSize: 18, fontWeight: 600 }}>Selecciona la plantilla</span>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    Elige el formato de reconocimiento que deseas generar
                  </span>
                </Stack>
              }
            >
              <Stack spacing={3} sx={{ mt: 1 }}>
                <BasicSelect
                  label="Plantilla"
                  name="idPlantilla"
                  id="idPlantilla"
                  options={cat_plantilla}
                  value={plantillaSeleccionada}
                  onChange={(e) => {
                    setPlantillaSeleccionada(e.target.value);
                    setErrorPlantilla(false);
                  }}
                  error={errorPlantilla}
                  errorMessage={errorPlantilla && 'Selecciona una plantilla'}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f9fafb',
                    },
                  }}
                  isLoading={isLoadingPlantilla}
                />

                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  Puedes generar un nuevo reconocimiento con la plantilla seleccionada
                </span>

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1.5}
                  sx={{
                    pt: 2,
                    borderTop: '1px solid #e5e7eb',
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setOpenPlantilla(false);
                      setPlantillaSeleccionada('');
                    }}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!plantillaSeleccionada) {
                        setErrorPlantilla(true);
                        return;
                      }

                      setErrorPlantilla(false);

                      generarReconocimientos(selectedUser, 2, plantillaSeleccionada);
                      setPlantillaSeleccionada('');
                      setOpenPlantilla(false);
                    }}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Continuar
                  </Button>
                </Stack>
              </Stack>
            </CustomModal>

            <Grid item xs={12}>
              <CustomTabs
                value={show}
                setValue={setShow}
                tabs={[
                  {
                    icon: 'group',
                    label: 'Registrados',
                    value: 0,
                    component: (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Nombre"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={search1.nombre}
                            onChange={(e) => setSearch1({ ...search1, nombre: e.target.value })}
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Celular"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={search1.celular}
                            onChange={(e) => setSearch1({ ...search1, celular: e.target.value })}
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4}>
                          <AdvancedSelect
                            label="Municipio"
                            name="Municipio"
                            placeholder="Municipio"
                            options={cat.municipios}
                            value={search1.idMunicipio}
                            onChange={(e) =>
                              setSearch1({ ...search1, idMunicipio: e.value, municipio: e.label })
                            }
                            isSearchable
                            isLoading={isLoadingCat}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <CustomTable
                            rows={filteredRowsInvited}
                            columns={columns}
                            pageSize={10}
                            isLoading={isLoading}
                            stickyHeader
                            maxHeight={400}
                          />
                        </Grid>
                      </Grid>
                    ),
                  },
                  {
                    icon: 'groups',
                    label: 'Asistieron',
                    value: 1,
                    component: (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Nombre"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={search2.nombre}
                            onChange={(e) => setSearch2({ ...search2, nombre: e.target.value })}
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Celular"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={search2.celular}
                            onChange={(e) => setSearch2({ ...search2, celular: e.target.value })}
                            disabled={isLoading}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4}>
                          <AdvancedSelect
                            label="Municipio"
                            name="Municipio"
                            placeholder="Municipio"
                            options={cat.municipios}
                            value={search2.idMunicipio}
                            onChange={(e) =>
                              setSearch2({ ...search2, idMunicipio: e.value, municipio: e.label })
                            }
                            isSearchable
                            isLoading={isLoadingCat}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <CustomTable
                            rows={filteredRowsAttended}
                            columns={columns}
                            pageSize={10}
                            isLoading={isLoading}
                            stickyHeader
                            maxHeight={400}
                          />
                        </Grid>
                      </Grid>
                    ),
                  },
                ]}
              />
            </Grid>
          </Grid>
        </>
      )}

      {type === 3 && (
        <EventForm
          type="edit"
          data={post}
          handleAction={handleClose}
          setType={setType}
          getEvents={getList}
          handleDelete={questionDelete}
          catalogsOut={cat}
          isLoadingCat={isLoadingCat}
        />
      )}
    </>
  );
};

CardListEvent.propTypes = {
  post: PropTypes.object.isRequired,
  index: PropTypes.number,
};

export default CardListEvent;
